import React, { useState, useEffect, useRef } from "react";
import {
  Moon,
  Heart,
  Gift,
  Lock,
  X,
  Star,
  Music,
  VolumeX,
  Sparkles,
} from "lucide-react";
import { firebaseStorage, adminStorage } from "../utils/firebase";
import { getRamadanDay } from "../utils/ramadanCalculator";
import RamadanCountdown from './RamadanCountdown';

const GiftView = ({ giftId }) => {
  const [gift, setGift] = useState(null);
  const [selectedLantern, setSelectedLantern] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [musicUrl, setMusicUrl] = useState("");
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [musicReady, setMusicReady] = useState(false);
  const audioRef = useRef(null);
  const hasInteracted = useRef(false);
  const currentDay = gift?.isDemo ? getRamadanDay(gift.demoDay) : getRamadanDay();

  useEffect(() => {
    const loadGift = async () => {
      const loadedGift = await firebaseStorage.getGift(giftId);

      if (loadedGift) {
        const isAdmin = localStorage.getItem("isAdminAuthenticated") === "true";

        if (
          !isAdmin &&
          loadedGift.planType !== "free" &&
          loadedGift.paymentStatus !== "approved"
        ) {
          setAccessDenied(true);
          return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get("token");

        if (loadedGift.hasPassword && loadedGift.password && !isAdmin) {
          if (urlToken && urlToken === loadedGift.accessToken) {
            sessionStorage.setItem(
              `gift_${giftId}_password`,
              loadedGift.password,
            );
            setGift(loadedGift);
            return;
          }

          const savedPassword = sessionStorage.getItem(
            `gift_${giftId}_password`,
          );
          if (savedPassword !== loadedGift.password) {
            setGift(loadedGift);
            setPasswordRequired(true);
            return;
          }
        }

        setGift(loadedGift);
      }
    };

    const loadMusic = async () => {
      const settings = await adminStorage.getSettings();
      setMusicUrl(settings.musicUrl || "");
      setMusicEnabled(settings.musicEnabled !== false);
    };

    loadGift();
    loadMusic();
  }, [giftId]);

  useEffect(() => {
    if (gift && musicUrl && musicEnabled && audioRef.current && !passwordRequired) {
      const audio = audioRef.current;

      const playAudio = async () => {
        try {
          await audio.play();
          setIsPlaying(true);
          setMusicReady(true);
        } catch (err) {
          console.log(
            "Auto-play prevented, waiting for user interaction:",
            err,
          );
          setMusicReady(true);
          const handleFirstInteraction = async () => {
            if (!hasInteracted.current) {
              hasInteracted.current = true;
              try {
                await audio.play();
                setIsPlaying(true);
              } catch (playErr) {
                console.log("Failed to play after interaction:", playErr);
              }
              document.removeEventListener("click", handleFirstInteraction);
              document.removeEventListener(
                "touchstart",
                handleFirstInteraction,
              );
            }
          };

          document.addEventListener("click", handleFirstInteraction);
          document.addEventListener("touchstart", handleFirstInteraction);
        }
      };

      playAudio();
    }
  }, [gift, musicUrl, musicEnabled, passwordRequired]);

  const toggleMusic = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
        hasInteracted.current = true;
      }
    } catch (err) {
      console.error("Error toggling music:", err);
    }
  };

  const handlePasswordSubmit = () => {
    if (gift && gift.password === passwordInput) {
      sessionStorage.setItem(`gift_${giftId}_password`, passwordInput);
      setPasswordRequired(false);
      setPasswordError("");
    } else {
      setPasswordError("كلمة السر غير صحيحة! 🔒");
    }
  };

  if (accessDenied) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-indigo-950 to-indigo-900 text-white flex items-center justify-center p-6 text-right"
        dir="rtl"
      >
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-sm w-full text-center border border-white/10 shadow-2xl">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <Gift className="w-full h-full text-yellow-400 animate-pulse" />
            <Lock className="w-8 h-8 text-white absolute -bottom-1 -right-1 bg-red-500 rounded-full p-1.5 border-2 border-indigo-950" />
          </div>

          <h2 className="text-2xl font-bold mb-3 text-white">
            هذه الهدية قيد التفعيل! 🕐
          </h2>

          <p className="text-purple-200 text-sm mb-8 leading-relaxed">
            صاحب الهدية لم يكمل إجراءات التفعيل بعد، أو أن الطلب ينتظر مراجعة
            الإدارة.
          </p>

          <div className="space-y-3">
            <a
              href="https://two-souls.vercel.app/"
              className="block w-full bg-yellow-400 hover:bg-yellow-500 text-indigo-950 font-bold py-3 rounded-xl transition-all active:scale-95"
            >
              تواصل مع الدعم
            </a>

            <p className="text-[10px] text-white/40 italic">
              إذا كنت صاحب الهدية، يرجى مراجعة حالة طلبك عبر كود التتبع.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (passwordRequired) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-950 to-indigo-900 text-white flex items-center justify-center p-6">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center border border-white/10 shadow-2xl">
          <div className="w-20 h-20 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-yellow-300" />
          </div>

          <h2 className="text-2xl font-bold mb-3 text-white">هدية محمية 🔒</h2>
          <p className="text-purple-200 text-sm mb-6">
            هذه الهدية محمية بكلمة سر. أدخل الكلمة للوصول للهدية
          </p>

          <div className="space-y-4">
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => {
                setPasswordInput(e.target.value);
                setPasswordError("");
              }}
              onKeyPress={(e) => e.key === "Enter" && handlePasswordSubmit()}
              placeholder="أدخل كلمة السر"
              className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 text-center"
            />

            {passwordError && (
              <p className="text-red-400 text-sm">{passwordError}</p>
            )}

            <button
              onClick={handlePasswordSubmit}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold py-3 rounded-lg transition-all"
            >
              فتح الهدية
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!gift) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 text-white flex items-center justify-center">
        <div className="text-center">
          <Moon className="w-16 h-16 mx-auto mb-4 text-yellow-300 animate-pulse" />
          <p className="text-xl">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const isLanternUnlocked = (day) => {
    if (localStorage.getItem("isAdminAuthenticated") === "true") return true;
    return currentDay >= day;
  };

  const isFree = gift.planType === "free";
  const gridClasses = isFree
    ? "flex flex-wrap justify-center items-center gap-6 md:gap-8"
    : "grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-5";

  const lanternSize = isFree
    ? "w-28 h-28 md:w-32 md:h-32"
    : "w-full max-w-[150px]";

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 text-white py-8 px-4 flex flex-col items-center">
      {musicUrl && musicEnabled && (
        <audio ref={audioRef} loop>
          <source src={musicUrl} type="audio/mpeg" />
        </audio>
      )}

      {musicUrl && musicEnabled && musicReady && (
        <button
          onClick={toggleMusic}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 rounded-full shadow-2xl flex items-center justify-center transition-all transform hover:scale-110 active:scale-95 border-2 border-purple-400/30"
          title={isPlaying ? "إيقاف الموسيقى" : "تشغيل الموسيقى"}
        >
          {isPlaying ? (
            <Music className="w-6 h-6 text-white animate-pulse" />
          ) : (
            <VolumeX className="w-6 h-6 text-white" />
          )}
        </button>
      )}

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <Star
            key={i}
            className="absolute text-yellow-200 opacity-40 animate-pulse"
            size={Math.random() * 6 + 3}
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto flex flex-col items-center">
        <div className="text-center mb-12 w-full">
          <Moon className="w-16 h-16 mx-auto mb-4 text-yellow-300" />
          <h1
            className="text-4xl md:text-5xl font-bold mb-2 flex items-center justify-center gap-2"
            dir="rtl"
          >
            <span>إلى:</span>
            <span dir="auto" className="mx-1">
              {gift.recipientName}
            </span>
          </h1>
          {gift.senderName && (
            <h3
              className="text-xl md:text-2xl text-yellow-400/90 font-medium mb-4 flex items-center justify-center gap-2"
              dir="rtl"
            >
              <span>من:</span>
              <span dir="auto" className="mx-1">
                {gift.senderName}
              </span>
              <Heart className="w-5 h-5 fill-current text-pink-500" />
            </h3>
          )}
          <h2 className="text-2xl md:text-3xl text-purple-200 mb-6">
            Ramadan Kareem
          </h2>
          <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-lg rounded-lg p-6">
            <p
              className="text-lg leading-relaxed whitespace-pre-wrap"
              dir="rtl"
            >
              {gift.openingMessage}
            </p>
          </div>
                
        </div>

        {/* TikTok Follow Button - للهدايا المجانية فقط */}
        {isFree && (
          <div className="w-full max-w-2xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-2xl p-6 border border-pink-400/30 shadow-xl">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                {/* النص والأيقونة */}
                <div className="flex items-center gap-4" dir="rtl">
                  <div className="bg-gradient-to-br from-pink-500 to-purple-600 p-3 rounded-full flex-shrink-0">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                  <div className="text-right">
                    <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                      تابعنا للمزيد من الهدايا! 🎁
                    </h3>
                    <p className="text-sm text-purple-200">
                      اكتشف هدايا رمضانية مميزة على صفحتنا
                    </p>
                  </div>
                </div>

                {/* زر المتابعة */}
                <a
                  href="https://www.tiktok.com/@two_2_souls"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold px-6 py-3 rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-lg flex items-center gap-2 whitespace-nowrap"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  <span>تابعنا</span>
                </a>
              </div>
            </div>
          </div>
        )}

<RamadanCountdown />
        <div
          className={`${gridClasses} mb-8 w-full ${isFree ? "max-w-3xl" : "justify-items-center"}`}
        >
          {gift.lanterns.map((lantern) => {
            const unlocked = isLanternUnlocked(lantern.day);
            return (
              <button
                key={lantern.day}
                onClick={() => unlocked && setSelectedLantern(lantern.day)}
                disabled={!unlocked}
                className={`${isFree ? lanternSize : "aspect-square " + lanternSize} rounded-lg p-4 flex flex-col items-center justify-center gap-2 transition-all ${
                  unlocked
                    ? "bg-gradient-to-br from-yellow-400/30 to-yellow-600/30 hover:from-yellow-400/50 hover:to-yellow-600/50 cursor-pointer transform hover:scale-105 shadow-lg shadow-yellow-900/20"
                    : "bg-white/5 opacity-50 cursor-not-allowed"
                }`}
              >
                {unlocked ? (
                  <Gift
                    className={`${isFree ? "w-12 h-12 md:w-14 md:h-14" : "w-8 h-8"} text-yellow-300 animate-pulse`}
                  />
                ) : (
                  <Gift
                    className={`${isFree ? "w-12 h-12 md:w-14 md:h-14" : "w-8 h-8"} text-gray-500 opacity-40`}
                  />
                )}
                <span
                  className={`${isFree ? "text-3xl md:text-4xl" : "text-2xl"} font-bold`}
                >
                  {lantern.day}
                </span>
                <span
                  className={`${isFree ? "text-sm" : "text-xs"} text-purple-200`}
                >
                  {unlocked ? "شوف هديتك" : "قريباً"}
                </span>
              </button>
            );
          })}
        </div>

        {selectedLantern !== null && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative shadow-2xl border border-white/10">
              <button
                onClick={() => setSelectedLantern(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-yellow-400/20 mb-4">
                  <span className="text-4xl font-bold text-yellow-300">
                    {selectedLantern}
                  </span>
                </div>
                <h3 className="text-2xl font-bold">
                  يوم {selectedLantern} من رمضان
                </h3>
                <p className="text-purple-200">
                  Day {selectedLantern} of Ramadan
                </p>
              </div>

              {gift.lanterns[selectedLantern - 1]?.imageURL && (
                <div className="relative w-full rounded-lg mb-6 overflow-hidden bg-black/20 flex items-center justify-center min-h-[250px] max-h-[450px]">
                  <div
                    className="absolute inset-0 blur-2xl opacity-40 scale-110"
                    style={{
                      backgroundImage: `url(${gift.lanterns[selectedLantern - 1].imageURL})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <img
                    src={gift.lanterns[selectedLantern - 1].imageURL}
                    alt={`Day ${selectedLantern}`}
                    className="relative z-10 max-w-full max-h-[450px] object-contain shadow-2xl transition-all duration-300"
                  />
                </div>
              )}

              <div className="bg-white/10 rounded-lg p-6 mb-6">
                <p
                  className="text-lg leading-relaxed whitespace-pre-wrap text-right"
                  dir="rtl"
                >
                  {gift.lanterns[selectedLantern - 1]?.message}
                </p>
              </div>

              {gift.lanterns[selectedLantern - 1]?.duaa && (
                <div
                  className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-6 w-full"
                  dir="rtl"
                >
                  <h4 className="flex items-center justify-start gap-2 text-xl font-bold mb-3 text-yellow-300">
                    <span>دعاء اليوم</span>
                    <Moon className="w-5 h-5" />
                  </h4>
                  <p className="text-lg leading-relaxed whitespace-pre-wrap text-right text-white">
                    {gift.lanterns[selectedLantern - 1].duaa}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-center mt-12 text-purple-300 w-full">
          <p className="text-sm">اليوم : {currentDay} رمضان</p>
          <p
            className="text-xs mt-2 italic flex items-center justify-center gap-1"
            dir="rtl"
          >
            <span>صنع بحب من</span>
            <span dir="auto" className="mx-1">
              {gift.senderName || "قلب محب"}
            </span>
            <Heart className="w-3 h-3 text-pink-500 fill-current" />
          </p>

          <div className="mt-8 mb-6">
            <a
              href="/"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold px-8 py-4 rounded-full shadow-2xl hover:shadow-yellow-500/50 transition-all transform hover:scale-105"
            >
              <Sparkles className="w-5 h-5" />
              <span>اصنع هديتك الآن!</span>
              <Sparkles className="w-5 h-5" />
            </a>
            <p className="text-xs text-purple-400 mt-3">
              شارك الحب مع من تحب في رمضان ❤️
            </p>
          </div>

          <div className="mt-8 group">
            <a
              href="https://www.tiktok.com/@two_2_souls"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 opacity-30 hover:opacity-100 transition-all"
            >
              <span className="text-[10px] tracking-[0.4em] text-gray-400 uppercase font-light">
                Developed by
              </span>
              <span className="text-white text-xs tracking-[0.3em] font-light group-hover:text-[#d4af37]">
                TWO <span className="font-bold">SOULS</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftView;
